import baseStyle from '@assets/styles/baseStyle.json';
import darkAtc from '@assets/styles/darkAtc.json';
import darkRoad from '@assets/styles/darkRoad.json';
import lightRoad from '@assets/styles/lightRoad.json';
import satellite from '@assets/styles/satellite.json';
import { colors } from '@theme/foundations/colors';
import { toLowercaseValueRecord } from '@utils/common.utils';
import { BBox, Feature } from 'geojson';
import { StyleSpecification } from 'react-map-gl/mapbox';

import { Coordinates } from '@/types/commons/commons.types';
import { ZoneTypeEnum } from '@/types/config/config.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { FeatureTypeEnum, LayerNameEnum, SensorLikeDisplayedOnMap } from '@/types/map.types';
import { InfoDataTypeEnum } from '@/types/popup.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import {
  IdentificationEnum,
  PolygonIdentificationEnum,
  StrobeIdentificationEnum,
} from '@/types/sensor/identification.types';
import { ExtendedSensor, SensorLikeEnum, SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';
import { FlightPlanStateEnum, LinkedDroneDetectionEnum } from '@/types/utm/flightPlan.types';

/**
 * Notre-Dame de Paris.
 */
export const PARIS: Coordinates = {
  longitude: 2.349966090270689,
  latitude: 48.85299971321042,
  type: '2D',
};

export const DEFAULT_BOUNDS: BBox = [2.274831259727121, 48.68306550795673, 2.483168740272879, 48.76293449204327];

export const mapStyles: { [key: string]: StyleSpecification } = {
  baseStyle: baseStyle as StyleSpecification,
  darkRoad: darkRoad as StyleSpecification,
  lightRoad: lightRoad as StyleSpecification,
  satellite: satellite as StyleSpecification,
  darkAtc: darkAtc as StyleSpecification,
};

export const POLYGONS_OPACITY = 0.5;
export const ZONE_POLYGONS_OPACITY = 0.4;
export const NO_FLY_ZONE_OPACITY = 0.3;
export const TARGET_STROBE_OPACITY = 0.4;

export enum ColorLabelEnum {
  ORANGE = 'ORANGE',
  YELLOW = 'YELLOW',
  RED = 'RED',
  GREEN = 'GREEN',
  PURPLE = 'PURPLE',
  WHITE = 'WHITE',
  GRAY = 'GRAY',
  BLACK = 'BLACK',
}

export const hexColor: Record<ColorLabelEnum, string> = {
  [ColorLabelEnum.ORANGE]: colors.orange.DEFAULT.value,
  [ColorLabelEnum.RED]: colors.red.DEFAULT.value,
  [ColorLabelEnum.GREEN]: colors.green.DEFAULT.value,
  [ColorLabelEnum.YELLOW]: colors.yellow.DEFAULT.value,
  [ColorLabelEnum.PURPLE]: colors.purple.DEFAULT.value,
  [ColorLabelEnum.WHITE]: colors.neutral.white.value,
  [ColorLabelEnum.GRAY]: colors.neutral['300'].value,
  [ColorLabelEnum.BLACK]: colors.neutral.black.value,
};

export const iconColor: Record<ColorLabelEnum, string> = {
  [ColorLabelEnum.RED]: 'rd',
  [ColorLabelEnum.ORANGE]: 'or',
  [ColorLabelEnum.YELLOW]: 'yl',
  [ColorLabelEnum.GREEN]: 'gn',
  [ColorLabelEnum.PURPLE]: 'pr',
  [ColorLabelEnum.WHITE]: 'wh',
  [ColorLabelEnum.GRAY]: 'gy',
  [ColorLabelEnum.BLACK]: 'bl',
};

export const SELECTED_FEATURE_COLOR = hexColor[ColorLabelEnum.WHITE];

export const targetColor: Record<ClassificationEnum, string> = {
  FRIEND: hexColor[ColorLabelEnum.GREEN],
  UNKNOWN: hexColor[ColorLabelEnum.GRAY],
  UNCLASSIFIED: hexColor[ColorLabelEnum.WHITE],
  WATCH: hexColor[ColorLabelEnum.YELLOW],
  SUSPECT: hexColor[ColorLabelEnum.ORANGE],
  ENEMY: hexColor[ColorLabelEnum.RED],
};

export const strobeColor: Record<StrobeIdentificationEnum, string> = {
  UNKNOWN: '#222',
  REMOTE_CONTROL: '#a427bf',
  DRONE: '#bf7027',
};

export const polygonColor: Record<PolygonIdentificationEnum, string> = {
  UNKNOWN: '#222',
  REMOTE_CONTROL: '#a427bf',
  DRONE: '#bf7027',
};

export type PolygonWithOutlineColor = { fill: string; outline: string };

export const zoneColor: Partial<Record<ZoneTypeEnum, PolygonWithOutlineColor>> = {
  [ZoneTypeEnum.NIZ]: { fill: '#6D91E5', outline: '#6D91E5' },
  [ZoneTypeEnum.IMZ]: { fill: '#4076E3', outline: '#4076E3' },
  [ZoneTypeEnum.NNZ]: { fill: '#6D91E5', outline: '#6D91E5' },
};

export const nfzColor: Record<DroneAlertLevelEnum, PolygonWithOutlineColor> = {
  [DroneAlertLevelEnum.DRONE_CRITICAL]: { fill: hexColor[ColorLabelEnum.RED], outline: hexColor[ColorLabelEnum.RED] },
  [DroneAlertLevelEnum.DRONE_WARNING]: {
    fill: hexColor[ColorLabelEnum.ORANGE],
    outline: hexColor[ColorLabelEnum.ORANGE],
  },
  [DroneAlertLevelEnum.DRONE_INFO]: { fill: hexColor[ColorLabelEnum.YELLOW], outline: hexColor[ColorLabelEnum.YELLOW] },
};

export const imzMarginColor: PolygonWithOutlineColor = { fill: '#6D91E5', outline: '#6D91E5' };

export const flightPlanFillColor: Record<FlightPlanStateEnum, string> = {
  [FlightPlanStateEnum.INACTIVE]: hexColor[ColorLabelEnum.GRAY],
  [FlightPlanStateEnum.ACTIVE]: hexColor[ColorLabelEnum.GREEN],
};

export const flightPlanBorderColor: Record<LinkedDroneDetectionEnum | FlightPlanStateEnum.INACTIVE, string> = {
  [LinkedDroneDetectionEnum.NO_DRONE_DETECTION]: hexColor[ColorLabelEnum.GREEN],
  [LinkedDroneDetectionEnum.DRONE_DETECTION_INSIDE]: hexColor[ColorLabelEnum.GREEN],
  [LinkedDroneDetectionEnum.DRONE_DETECTION_OUTSIDE]: hexColor[ColorLabelEnum.YELLOW],
  [FlightPlanStateEnum.INACTIVE]: hexColor[ColorLabelEnum.GRAY],
};

export const sensorShorthand: Record<SensorTypeEnum, string> = {
  ADSB: 'AD',
  AEROSCOPE: 'AER',
  ARDRONIS: 'ARD',
  AUTOMATON: 'API',
  AVISO: 'AVI',
  BLAST: 'BST',
  DFB6: 'D6',
  DRONE_SHIELD: 'RF',
  FLIR: 'FLR',
  GENERIC: 'EXT',
  GROUND_STATION: 'GCS',
  HOLODRONE: 'HLD',
  AF2206: 'HLP',
  AXIS: 'AXS',
  MAJES: 'THREEB',
  ROBIN_MAX: 'RBN',
  ONVIF: 'CAM',
  SENID: 'SEN',
  SKY_EYE: 'SKY',
  SKY_JACKER: 'SKJ',
  SPEXER: 'SPX',
  XENTA: 'XTA',
  ZMER: 'ZMR',
};

export const subSensorShortHand: Record<SubSensorTypeEnum, string> = {
  AUTOMATON_CABINET: 'BT',
  ARDRONIS_ANTENNA: 'ARD',
  GENERIC_SUB_SENSOR: 'EXT',
  ROBIN_MAX_RADAR: 'RBN',
  SENID_ANTENNA: 'SEN',
  SKY_EYE_ANTENNA: 'SKY',
  SKY_JACKER_ANTENNA: 'SKJ',
  SPEXER_RADAR: 'SPX',
  AUTOMATON_SEGMENT: 'TRO',
};

export const sensorLikeShortHand: Record<SensorLikeEnum, string> = {
  PERIMETER_CAMERA: 'P-CAM',
};

export const extendedSensorShortHand: Record<ExtendedSensor, string> = {
  ...sensorShorthand,
  ...subSensorShortHand,
  ...sensorLikeShortHand,
};

export const extendedSensorIconShorthand: Record<SensorLikeDisplayedOnMap, Lowercase<string>> = {
  ...toLowercaseValueRecord(sensorShorthand),
  ...toLowercaseValueRecord(subSensorShortHand),
  ...toLowercaseValueRecord(sensorLikeShortHand),
};

export const ZONE_LAYER_NAMES = [
  LayerNameEnum.INITIALIZATION_MASKING_ZONE_FILLS,
  LayerNameEnum.INITIALIZATION_MASKING_ZONE_OUTLINES,
  LayerNameEnum.NO_FLY_ZONE_FILLS,
  LayerNameEnum.NO_FLY_ZONE_OUTLINES,
  LayerNameEnum.NO_INIT_ZONE_FILLS,
  LayerNameEnum.NO_INIT_ZONE_OUTLINES,
];

export const AUTOMATON_SECTION_LAYER_NAMES = [
  LayerNameEnum.AUTOMATON_SECTIONS,
  LayerNameEnum.AUTOMATON_SECTION_EXTREMITIES,
  LayerNameEnum.AUTOMATON_SECTION_ICONS,
  LayerNameEnum.AUTOMATON_SECTION_LABELS,
  LayerNameEnum.AUTOMATON_SECTION_OUTLINES,
];

export const AUTOMATON_SECTION_FEATURE_TYPES = [
  FeatureTypeEnum.AUTOMATON_SECTION_POINT,
  FeatureTypeEnum.AUTOMATON_SECTION_ICON,
  FeatureTypeEnum.AUTOMATON_SECTION_LABEL,
  FeatureTypeEnum.AUTOMATON_SECTION_LINE,
];

export const sensorIconColorFromSensorStatusAndState = {
  UNREACHABLE: iconColor[ColorLabelEnum.PURPLE],
  SLEEP: iconColor[ColorLabelEnum.GRAY],
  EFFRACTION: iconColor[ColorLabelEnum.RED],
  DEGRADED: iconColor[ColorLabelEnum.ORANGE],
  KO: iconColor[ColorLabelEnum.ORANGE],
  HEALTHY: iconColor[ColorLabelEnum.GREEN],
};

export const automatonSectionColor = {
  ...sensorIconColorFromSensorStatusAndState,
  HT_DETECTION: iconColor[ColorLabelEnum.GREEN],
  BT_DETECTION: iconColor[ColorLabelEnum.GREEN],
  UNREACHABLE: iconColor[ColorLabelEnum.WHITE],
};

export const automatonSectionColorHexa = {
  UNREACHABLE: hexColor[ColorLabelEnum.WHITE],
  SLEEP: hexColor[ColorLabelEnum.GRAY],
  EFFRACTION: hexColor[ColorLabelEnum.RED],
  DEGRADED: hexColor[ColorLabelEnum.ORANGE],
  KO: hexColor[ColorLabelEnum.ORANGE],
  HEALTHY: hexColor[ColorLabelEnum.GREEN],
};

export const PERIMETER_CAMERA_FILL_COLOR = '#0097b7';
export const SELECTED_PERIMETER_CAMERA_FILL_COLOR = hexColor[ColorLabelEnum.WHITE];

export const ANIMATION_DURATION = 300;
export const PITCH_FLAT = 0;
export const BEARING_NORTH = 0;
export const PITCH_TILT = 60;

export const featureTypeToInfoDataType: Partial<Record<FeatureTypeEnum, InfoDataTypeEnum>> = {
  [FeatureTypeEnum.FLIGHT_PLAN]: InfoDataTypeEnum.INFO_FLIGHT_PLAN,

  [FeatureTypeEnum.PERIMETER_CAMERA_LABEL]: InfoDataTypeEnum.INFO_PERIMETER_CAMERA,
  [FeatureTypeEnum.PERIMETER_CAMERA_SECTOR]: InfoDataTypeEnum.INFO_PERIMETER_CAMERA,

  [FeatureTypeEnum.AUTOMATON_SECTION_POINT]: InfoDataTypeEnum.INFO_AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_ICON]: InfoDataTypeEnum.INFO_AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_LABEL]: InfoDataTypeEnum.INFO_AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_LINE]: InfoDataTypeEnum.INFO_AUTOMATON_SECTION,

  [FeatureTypeEnum.SENSOR_ICON]: InfoDataTypeEnum.INFO_SENSOR,
  [FeatureTypeEnum.SENSOR_LABEL]: InfoDataTypeEnum.INFO_SENSOR,

  [FeatureTypeEnum.STROBE_SECTOR]: InfoDataTypeEnum.INFO_STROBE,

  [FeatureTypeEnum.POLYGON_LOCATION]: InfoDataTypeEnum.INFO_POLYGON,
  [FeatureTypeEnum.POLYGON_LOCATION_OUTLINE]: InfoDataTypeEnum.INFO_POLYGON,

  [FeatureTypeEnum.TARGET_ICON]: InfoDataTypeEnum.INFO_TARGET,
};

export const orientedTarget = [IdentificationEnum.AIRCRAFT, IdentificationEnum.VCA];

export const DEFAULT_THRESHOLD = 120;

export const EMPTY_FEATURES: Feature[] = [];

export const FLIGHT_PLAN_LAYERS: LayerNameEnum[] = [
  LayerNameEnum.FLIGHT_PLAN_FILLS,
  LayerNameEnum.FLIGHT_PLAN_OUTLINES,
  LayerNameEnum.FLIGHT_PLAN_INTERSECTION_FILLS,
  LayerNameEnum.FLIGHT_PLAN_INTERSECTION_OUTLINES,
];
